import { Alert, Box, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../App/store/store';
import { fetchToolOwnerTools, selectHeiUser } from '../../../../Auth/store/accountSlice';
import { ToolInfoDto } from '../../../UserSearch/api/models/ToolInfoDto';
import {
    addToolManager,
    getToolManagers,
    removeToolManager,
} from '../../api/adminToolManagerClient';
import { ToolManagerDto, ToolManagerRole } from '../../api/models/ToolManagerDto';
import AdminToolManagerSearch from './AdminToolManagerSearch';
import AdminToolManagersGrid from './AdminToolManagersGrid';

interface AdminToolManagerManagementProps {
    tool: ToolInfoDto;
}

const AdminToolManagerManagement = ({ tool }: AdminToolManagerManagementProps): JSX.Element => {
    const [members, setMembers] = useState<ToolManagerDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const heiUser = useSelector(selectHeiUser);
    const dispatch = useDispatch<AppDispatch>();

    const getErrorMessage = (httpError: unknown, fallback: string): string => {
        if (axios.isAxiosError(httpError)) {
            const responseData = httpError.response?.data;
            if (typeof responseData === 'string') {
                return responseData;
            }
        }

        return fallback;
    };

    useEffect(() => {
        loadMembers();
    }, [tool.toolId]);

    const loadMembers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getToolManagers(tool.toolId);
            setMembers(data);
        } catch (err: unknown) {
            setError(getErrorMessage(err, 'Failed to load tool managers'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddMember = async (
        name: string,
        email: string,
        adPrinciple: string,
        role: ToolManagerRole,
    ) => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await addToolManager({
                toolId: tool.toolId,
                memberName: name,
                memberEmail: email,
                memberAdPrinciple: adPrinciple,
                role: role,
            });

            setSuccess(`Successfully added ${name} as ${role}`);
            refreshLoggedUser(email);
            await loadMembers();
        } catch (err: unknown) {
            setError(getErrorMessage(err, 'Failed to add member'));
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async (member: ToolManagerDto) => {
        if (
            !window.confirm(
                `Are you sure you want to remove ${member.memberName} as ${member.role}?`,
            )
        ) {
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await removeToolManager({
                toolId: tool.toolId,
                memberId: member.memberId,
                role: member.role as ToolManagerRole,
            });

            setSuccess(`Successfully removed ${member.memberName}`);
            refreshLoggedUser(member.memberEmail);
            await loadMembers();
        } catch (err: unknown) {
            setError(getErrorMessage(err, 'Failed to remove member'));
        } finally {
            setSaving(false);
        }
    };

    const refreshLoggedUser = (memberEmail: string) => {
        if (heiUser?.email === memberEmail) {
            dispatch(fetchToolOwnerTools());
        }
    };

    return (
        <Box sx={{ pt: 3 }}>
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Typography variant='h6' sx={{ mb: 2 }}>
                Add Manager
            </Typography>
            <AdminToolManagerSearch
                onAddMember={handleAddMember}
                existingMembers={members.map((member) => ({
                    email: member.memberEmail,
                    adPrinciple: member.memberEmail,
                }))}
                disabled={saving}
            />

            <Typography variant='h6' sx={{ mb: 2, mt: 2 }}>
                Current Members
            </Typography>
            <AdminToolManagersGrid
                members={members}
                loading={loading}
                saving={saving}
                onRemoveMember={handleRemoveMember}
            />
        </Box>
    );
};

export default AdminToolManagerManagement;
